import type { AuthSuccess } from "@reelio/contracts";
import {
  BarChart3,
  Brain,
  CalendarDays,
  ChevronDown,
  FlaskConical,
  Library,
  LogOut,
  MessageCircle,
  PlusCircle,
  Settings,
  Sparkles,
  Workflow,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

export type DashboardSection =
  | "today"
  | "pipeline"
  | "brand"
  | "research"
  | "calendar"
  | "analytics";

type SystemNavigationItem = {
  id: DashboardSection;
  label: string;
  icon: LucideIcon;
};

const contentSystemNavigation: SystemNavigationItem[] = [
  { id: "research", label: "Research lab", icon: FlaskConical },
  { id: "pipeline", label: "Content pipeline", icon: Workflow },
  { id: "brand", label: "Brand brain", icon: Brain },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const recentRuns = [
  "Competitor hook scan",
  "7-day content plan",
  "Product demo concepts",
  "Instagram launch calendar",
] as const;

type DashboardSidebarProps = {
  viewer: AuthSuccess;
  activeSection: DashboardSection;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onNewBrief: () => void;
  onSelect: (section: DashboardSection) => void;
  onSignOut: () => void;
};

type SidebarDropdownProps = {
  children: ReactNode;
  className?: string;
  id?: string;
  open: boolean;
  variant: "disclosure" | "popover";
};

const SidebarDropdown = ({
  children,
  className,
  id,
  open,
  variant,
}: SidebarDropdownProps) => (
  <div
    id={id}
    className={[
      `dashboard-sidebar__${variant}`,
      className,
      open ? "is-open" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    aria-hidden={!open}
  >
    {children}
  </div>
);

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "R";

export const DashboardSidebar = ({
  viewer,
  activeSection,
  mobileOpen,
  onCloseMobile,
  onNewBrief,
  onSelect,
  onSignOut,
}: DashboardSidebarProps) => {
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [contentSystemOpen, setContentSystemOpen] = useState(true);
  const [recentRunsOpen, setRecentRunsOpen] = useState(true);
  const sidebarRef = useRef<HTMLElement>(null);
  const initials = getInitials(viewer.workspace.name);

  const closeMenus = () => {
    setWorkspaceMenuOpen(false);
    setAccountMenuOpen(false);
  };

  const selectSection = (section: DashboardSection) => {
    closeMenus();
    onSelect(section);
    onCloseMobile();
  };

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        closeMenus();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <>
      <button
        type="button"
        className={
          mobileOpen
            ? "dashboard-sidebar__scrim is-visible"
            : "dashboard-sidebar__scrim"
        }
        aria-label="Close navigation"
        onClick={onCloseMobile}
      />

      <aside
        ref={sidebarRef}
        className={
          mobileOpen
            ? "dashboard-sidebar is-mobile-open"
            : "dashboard-sidebar"
        }
        data-theme="light"
        aria-label="Dashboard navigation"
      >
        <div className="dashboard-sidebar__project-row">
          <a
            className="dashboard-sidebar__mark"
            href="/"
            aria-label="Reelio home"
          >
            <Sparkles size={17} fill="currentColor" aria-hidden="true" />
          </a>
          <div className="dashboard-sidebar__workspace-wrap">
            <button
              type="button"
              className="dashboard-sidebar__project"
              aria-label="Switch workspace"
              aria-expanded={workspaceMenuOpen}
              onClick={() => {
                setWorkspaceMenuOpen((open) => !open);
                setAccountMenuOpen(false);
              }}
            >
              <strong>{viewer.workspace.name}</strong>
              <ChevronDown
                className={workspaceMenuOpen ? "is-rotated" : ""}
                size={14}
                aria-hidden="true"
              />
            </button>

            <SidebarDropdown
              className="dashboard-sidebar__workspace-menu"
              open={workspaceMenuOpen}
              variant="popover"
            >
              <button
                type="button"
                className="dashboard-sidebar__workspace-option"
                tabIndex={workspaceMenuOpen ? 0 : -1}
                aria-current="true"
                onClick={() => setWorkspaceMenuOpen(false)}
              >
                {viewer.workspace.name}
              </button>
            </SidebarDropdown>
          </div>
          <button
            className="dashboard-sidebar__mobile-close"
            type="button"
            aria-label="Close navigation"
            onClick={onCloseMobile}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="dashboard-sidebar__modes" aria-label="Workspace mode">
          <button
            type="button"
            className={activeSection === "today" ? "is-active" : ""}
            onClick={onNewBrief}
          >
            <Sparkles size={14} aria-hidden="true" />
            Autopilot
          </button>
          <button
            type="button"
            className={activeSection === "pipeline" ? "is-active" : ""}
            onClick={() => selectSection("pipeline")}
          >
            <Library size={14} aria-hidden="true" />
            Content
          </button>
        </div>

        <div className="dashboard-sidebar__scroll">
          <nav className="dashboard-sidebar__nav" aria-label="Workspace">
            <button
              type="button"
              className={
                activeSection === "today"
                  ? "dashboard-sidebar__nav-item is-active"
                  : "dashboard-sidebar__nav-item"
              }
              aria-current={activeSection === "today" ? "page" : undefined}
              onClick={onNewBrief}
            >
              <PlusCircle size={17} aria-hidden="true" />
              <span>New brief</span>
            </button>
            <button
              type="button"
              className="dashboard-sidebar__nav-item"
              onClick={() => selectSection("pipeline")}
            >
              <MessageCircle size={17} aria-hidden="true" />
              <span>All agent runs</span>
            </button>
            <button
              type="button"
              className={
                activeSection === "calendar"
                  ? "dashboard-sidebar__nav-item is-active"
                  : "dashboard-sidebar__nav-item"
              }
              onClick={() => selectSection("calendar")}
            >
              <CalendarDays size={17} aria-hidden="true" />
              <span>Scheduled</span>
            </button>
            <button
              type="button"
              className="dashboard-sidebar__nav-item"
              aria-expanded={contentSystemOpen}
              aria-controls="dashboard-content-system"
              onClick={() => setContentSystemOpen((open) => !open)}
            >
              <Workflow size={17} aria-hidden="true" />
              <span>Content system</span>
              <ChevronDown
                className={contentSystemOpen ? "is-rotated" : ""}
                size={14}
                aria-hidden="true"
              />
            </button>

            <SidebarDropdown
              id="dashboard-content-system"
              open={contentSystemOpen}
              variant="disclosure"
            >
              <div className="dashboard-sidebar__nested">
                {contentSystemNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  return (
                    <button
                      key={item.label}
                      type="button"
                      className={active ? "is-active" : ""}
                      tabIndex={contentSystemOpen ? 0 : -1}
                      onClick={() => selectSection(item.id)}
                    >
                      <Icon size={15} aria-hidden="true" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </SidebarDropdown>
          </nav>

          <section className="dashboard-sidebar__recent">
            <button
              type="button"
              className="dashboard-sidebar__recent-heading"
              aria-expanded={recentRunsOpen}
              aria-controls="dashboard-recent-runs"
              onClick={() => setRecentRunsOpen((open) => !open)}
            >
              Recent runs
              <ChevronDown
                className={recentRunsOpen ? "is-rotated" : ""}
                size={13}
                aria-hidden="true"
              />
            </button>
            <SidebarDropdown
              id="dashboard-recent-runs"
              open={recentRunsOpen}
              variant="disclosure"
            >
              <div className="dashboard-sidebar__recent-list">
                {recentRuns.map((run) => (
                  <button
                    key={run}
                    type="button"
                    tabIndex={recentRunsOpen ? 0 : -1}
                    onClick={() => selectSection("pipeline")}
                  >
                    {run}
                  </button>
                ))}
              </div>
            </SidebarDropdown>
          </section>
        </div>

        <div className="dashboard-sidebar__footer">
          <nav className="dashboard-sidebar__utility" aria-label="Settings">
            <button type="button" onClick={() => selectSection("brand")}>
              <Settings size={17} aria-hidden="true" />
              Settings
            </button>
          </nav>

          <div className="dashboard-sidebar__account-wrap">
            <button
              type="button"
              className="dashboard-sidebar__account"
              aria-expanded={accountMenuOpen}
              onClick={() => {
                setAccountMenuOpen((open) => !open);
                setWorkspaceMenuOpen(false);
              }}
            >
              <span className="dashboard-sidebar__account-avatar">{initials}</span>
              <span>
                <strong>{viewer.workspace.name}</strong>
                <small>{viewer.user.email}</small>
              </span>
              <ChevronDown
                className={accountMenuOpen ? "is-rotated" : ""}
                size={15}
                aria-hidden="true"
              />
            </button>

            <SidebarDropdown
              className="dashboard-sidebar__account-menu"
              open={accountMenuOpen}
              variant="popover"
            >
              <div>
                <span>Signed in as</span>
                <strong>{viewer.user.email}</strong>
              </div>
              <button
                type="button"
                tabIndex={accountMenuOpen ? 0 : -1}
                onClick={() => selectSection("brand")}
              >
                <Brain size={15} aria-hidden="true" />
                Account & brand
              </button>
              <button
                type="button"
                tabIndex={accountMenuOpen ? 0 : -1}
                onClick={onSignOut}
              >
                <LogOut size={15} aria-hidden="true" />
                Log out
              </button>
            </SidebarDropdown>
          </div>
        </div>
      </aside>
    </>
  );
};
